/**
 * Assembling a TanStack router from the feature registry.
 *
 * Two things happen here, and only these two: the collected routes become a
 * route tree, and every route gets the same `beforeLoad` that runs the gates.
 * Uniform application is the point — a guard you have to remember to attach is
 * a guard that will eventually be forgotten on the one screen that needed it.
 *
 * The gate context (is anyone signed in? what may they do?) is supplied by the
 * application through the router's context, not read from a module-level
 * singleton, so a test can drive a router through any principal without
 * mounting providers or mutating globals.
 */

import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import type { FunctionComponent } from "react";
import { AccessSnapshot } from "@hardauto/core-access";

import { evaluateGates, type Gate } from "./gates";
import type { RouteDefinition } from "./registry";

/** What the gates are given on every navigation. */
export interface RouterContext {
  sessionReady: boolean;
  authenticated: boolean;
  accessReady: boolean;
  snapshot: AccessSnapshot;
}

/** Context for a router that has not been told anything yet.
 *
 * `sessionReady: false` is the safe starting point: the bootstrap gate holds
 * navigation until the application says otherwise, so a router created before
 * the session is restored cannot briefly render a private screen.
 */
export const INITIAL_CONTEXT: RouterContext = {
  sessionReady: false,
  authenticated: false,
  accessReady: false,
  snapshot: AccessSnapshot.empty(),
};

export interface CreateAppRouterOptions {
  routes: readonly RouteDefinition[];
  /** Chrome rendered around every route. Defaults to a bare `<Outlet/>`. */
  rootComponent?: FunctionComponent;
  /** Rendered when no route matches. */
  notFoundComponent?: FunctionComponent;
  /** Override for tests; production uses the default set. */
  gates?: readonly Gate[];
  /**
   * The gate context to start with.
   *
   * Supply the REAL one. The default is deliberately fail-closed, and a router
   * built with it evaluates its first navigation as "nobody is signed in" —
   * which sends a permitted user to the bootstrap route and leaves them there,
   * because gates can only redirect.
   */
  context?: RouterContext;
}

/**
 * Build the application router.
 *
 * **The caller must not render this router until the session is resolved**, and
 * must unmount it again while permissions are being re-fetched. The gates can
 * only redirect, and redirecting for "not known yet" throws away the URL the
 * user asked for — a deep link into the application becomes a spinner they
 * cannot leave.
 *
 * Unmounting has none of that cost: the browser URL is untouched, so the
 * router re-reads it and re-evaluates the gates the moment it comes back.
 */
export function createAppRouter({
  routes,
  rootComponent,
  notFoundComponent,
  gates,
  context = INITIAL_CONTEXT,
}: CreateAppRouterOptions) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: rootComponent ?? Outlet,
    ...(notFoundComponent ? { notFoundComponent } : {}),
  });

  const children = routes.map((definition) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: definition.path,
      component: definition.component,

      // The single place navigation is authorised. Every route gets it,
      // including the ones with no requirement — those simply return early,
      // and having no exception means there is no route to forget.
      beforeLoad: ({ context, location }) => {
        const verdict = evaluateGates(
          {
            location: location.href,
            sessionReady: context.sessionReady,
            authenticated: context.authenticated,
            accessReady: context.accessReady,
            snapshot: context.snapshot,
            requirement: definition.requirement,
          },
          gates,
        );

        if (verdict) {
          // `throw redirect` is TanStack's control flow, not an error: it
          // aborts the load before the component or its data are fetched, so
          // a forbidden screen never issues its requests.
          // TanStack's `redirect` is a control-flow signal, not an error; the
          // rule is right in general and stays on everywhere else.
          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw redirect({ to: verdict.redirectTo, replace: true });
        }
      },
    }),
  );

  // TanStack builds its route tree types by inference from a literal tree; a
  // registry assembles one at runtime, from an array whose shape is not known
  // until the features are imported. The two cannot both be satisfied, and the
  // trade is deliberate: we keep features able to contribute routes without the
  // composition root naming them, and give up per-route path autocompletion.
  //
  // What is given up is only autocompletion. `RouteDefinition.path` is still
  // checked, duplicates still fail at startup (`collectRoutes`), and the gates
  // still run on every route — so a wrong path is a 404 in development, not a
  // silent hole. Narrow, single-line, and explained rather than repo-wide.
  return createRouter({
    // @ts-expect-error -- see the note above: a runtime-assembled tree cannot
    // satisfy types inferred from a literal one under exactOptionalPropertyTypes.
    routeTree: rootRoute.addChildren(children as never),
    context,
    // A stale screen behind a spinner beats a blank one: TanStack keeps the
    // previous match rendered for this long before showing the pending state.
    defaultPendingMs: 300,
    defaultPendingMinMs: 300,
  });
}

/** The router this factory produces. */
export type AppRouter = ReturnType<typeof createAppRouter>;
